<template>
  <section class="algorithm-panel trend-panel">
    <div class="panel-title">模糊聚类分层趋势图</div>
    <div ref="chartRef" class="trend-chart"></div>
    <table class="trend-table">
      <tbody>
        <tr v-for="row in tableRows" :key="row.day">
          <td>{{ row.day }}</td>
          <td>{{ row.crop }}</td>
          <td>{{ row.cluster }}</td>
          <td>{{ row.advice }}</td>
          <td>{{ row.yield }}</td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script setup>
import * as echarts from "echarts";
import { onBeforeUnmount, onMounted, ref } from "vue";

const chartRef = ref(null);
let chartInstance = null;

function formatDateLabel(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}-${day}`;
}

function getRecentDateLabels() {
  const today = new Date();
  return [18, 15, 11, 8, 5, 3, 0].map((offset) => {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    return formatDateLabel(date);
  });
}

const dateLabels = getRecentDateLabels();

const tableRows = [
  { day: "日期", crop: "作物品类", cluster: "聚类判定", advice: "管护措施", yield: "长势指数" },
  { day: dateLabels[3], crop: "小麦", cluster: "轻度胁迫", advice: "补水降温", yield: "68" },
  { day: dateLabels[5], crop: "小麦", cluster: "正常区间", advice: "均衡追肥", yield: "72" },
  { day: dateLabels[6], crop: "小麦", cluster: "优良区间", advice: "常规巡田", yield: "78" },
];

const option = {
  color: ["#5fffee", "#8ff8ff", "#d8ff5f", "#ffcf5f", "#ff705f"],
  grid: { top: 86, left: 74, right: 52, bottom: 36 },
  legend: {
    top: 10,
    left: 80,
    right: 12,
    itemWidth: 18,
    itemHeight: 8,
    itemGap: 14,
    textStyle: { color: "#e3ffff", fontSize: 24, fontWeight: "bold" },
    data: ["优良区间", "正常区间", "偏弱区间", "轻度胁迫", "重度胁迫"],
  },
  tooltip: {
    trigger: "axis",
    backgroundColor: "rgba(12, 54, 58, .92)",
    borderColor: "rgba(95, 255, 240, .52)",
    textStyle: { color: "#fff", fontSize: 22 },
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: dateLabels,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "rgba(145, 255, 247, .4)" } },
    axisLabel: { color: "#dfffff", fontSize: 22, fontWeight: "bold" },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: 100,
    interval: 20,
    axisLabel: { color: "#dfffff", fontSize: 22, fontWeight: "bold" },
    splitLine: { lineStyle: { color: "rgba(111, 238, 227, .18)", type: "dashed" } },
  },
  series: [
    {
      name: "优良区间",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { width: 2, color: "#5fffee" },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgba(95, 255, 238, .72)" },
          { offset: 0.7, color: "rgba(31, 188, 178, .25)" },
          { offset: 1, color: "rgba(31, 188, 178, .02)" },
        ]),
      },
      data: [76, 78, 80, 82, 84, 85, 87],
    },
    {
      name: "正常区间",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { width: 2, color: "#8ff8ff" },
      data: [62, 64, 66, 68, 70, 72, 74],
    },
    {
      name: "偏弱区间",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { width: 2, color: "#d8ff5f" },
      data: [48, 50, 52, 53, 55, 57, 58],
    },
    {
      name: "轻度胁迫",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { width: 2, color: "#ffcf5f" },
      data: [34, 36, 38, 40, 42, 41, 39],
    },
    {
      name: "重度胁迫",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { width: 2, color: "#ff705f" },
      data: [18, 20, 22, 21, 19, 17, 15],
    },
  ],
};

function resizeChart() {
  chartInstance && chartInstance.resize();
}

onMounted(() => {
  chartInstance = echarts.init(chartRef.value);
  chartInstance.setOption(option);
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  chartInstance && chartInstance.dispose();
});
</script>

<style lang="less" scoped>
.algorithm-panel {
  position: relative;
  min-height: 0;
  padding: 40px 48px 42px;
  box-sizing: border-box;
  border: 1px solid rgba(64, 255, 238, 0.72);
  background:
    radial-gradient(circle at 50% 100%, rgba(13, 173, 157, 0.18), transparent 40%),
    linear-gradient(180deg, rgba(4, 51, 50, 0.5), rgba(3, 25, 31, 0.82));
  box-shadow: inset 0 0 34px rgba(30, 242, 224, 0.13), 0 0 16px rgba(0, 229, 214, 0.13);
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.algorithm-panel::before,
.algorithm-panel::after {
  content: "";
  position: absolute;
  width: 22px;
  height: 22px;
  border-color: #54fff0;
  border-style: solid;
}

.algorithm-panel::before {
  left: -1px;
  bottom: -1px;
  border-width: 0 0 3px 3px;
}

.algorithm-panel::after {
  right: -1px;
  top: -1px;
  border-width: 3px 3px 0 0;
}

.panel-title {
  position: relative;
  padding-left: 26px;
  color: #f3fcff;
  font-size: 34px;
  font-weight: bold;
  text-align: left;
}

.panel-title::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 0;
  width: 6px;
  height: 40px;
  background: #5ffff0;
  box-shadow: 0 0 12px rgba(95, 255, 240, 0.8);
}

.trend-panel {
  display: flex;
  flex-direction: column;
}

.trend-chart {
  width: 100%;
  height: 58%;
  min-height: 0;
}

.trend-table {
  width: 100%;
  border-collapse: collapse;
  color: #eaffff;
  font-size: 23px;
  font-weight: bold;
  margin-top: 30px;

  td {
    height: 60px;
    border-top: 1px solid rgba(113, 255, 241, 0.28);
    text-align: center;
  }
}
</style>
